import * as C from '../constants';
import TwentyService from '../../../services/twenty-service';
import CarrierTypes from '../../../variables/carrierTypes';
import ProductTypesIds from '../../../utils/productTypesIds';
import Domains from '../../../variables/domains';

export default {
  /**
   * @param commit
   * @param payload
   * @returns {Promise<T>}
   */
  getSuppliersWithCurrencyBySupplierType({ commit }, domains) {
    commit(C.NON_DYNAMIC_TRANSPORT_SUPPLIERS_PENDING);
    let masterId = ProductTypesIds.ELCANO_MASTER;
    if (Domains.shortNames.CADDIE_DOMAINS.includes(domains[0].domain.shortName)) {
      masterId = ProductTypesIds.CADDIE_MASTER;
    }
    if (Domains.shortNames.BE_ACTIVE_AWAY_DOMAINS.includes(domains[0].domain.shortName)) {
      masterId = ProductTypesIds.BE_ACTIVE_AWAY_MASTER;
    }
    return TwentyService.getSuppliersWithCurrencyBySupplierType(masterId)
      .then(res => {
        commit(C.NON_DYNAMIC_TRANSPORT_SUPPLIERS_SUCCESS, res.data);
      }).catch(() => {
        commit(C.NON_DYNAMIC_TRANSPORT_SUPPLIERS_FAILURE);
      });
  },

  getSuppliersWithCurrency({ commit }) {
    commit(C.NON_DYNAMIC_TRANSPORT_SUPPLIERS_PENDING);
    const requests = [
      TwentyService.getSuppliersWithCurrencyBySupplierType(ProductTypesIds.ELCANO_MASTER),
      TwentyService.getSuppliersWithCurrencyBySupplierType(ProductTypesIds.CADDIE_MASTER),
      TwentyService.getSuppliersWithCurrencyBySupplierType(ProductTypesIds.BE_ACTIVE_AWAY_MASTER),
      TwentyService.getSuppliersWithCurrencyBySupplierType(ProductTypesIds.SKI_MASTER),
    ];
    Promise.all(requests).then(res => {
      const suppliers = res.map(r => r.data).flat();
      commit(C.NON_DYNAMIC_TRANSPORT_SUPPLIERS_SUCCESS, suppliers);
    }).catch(() => {
      commit(C.NON_DYNAMIC_TRANSPORT_SUPPLIERS_FAILURE);
    });
  },

  /**
   * @param commit
   * @returns {Promise<T | never>}
   */
  getTransitsByType({ commit }) {
    commit(C.NON_DYNAMIC_TRANSPORT_TRANSITS_PENDING);
    return TwentyService.getTransitsByType(CarrierTypes.AIRPORT)
      .then(res => {
        commit(C.NON_DYNAMIC_TRANSPORT_TRANSITS_SUCCESS, res.data);
      }).catch(() => {
        commit(C.NON_DYNAMIC_TRANSPORT_SUPPLIERS_FAILURE);
      });
  },

  /**
   * @param commit
   * @returns {Promise<T | never>}
   */
  getCarriers({ commit }) {
    commit(C.NON_DYNAMIC_TRANSPORT_CARRIERS_PENDING);
    return TwentyService.getCarriers()
      .then(res => {
        if (res.status === 200) {
          commit(C.NON_DYNAMIC_TRANSPORT_CARRIERS_SUCCESS, res.data);
        } else if (res.status === 204) {
          commit(C.NON_DYNAMIC_TRANSPORT_CARRIERS_NO_CONTENT);
        }
      }).catch(() => {
        commit(C.NON_DYNAMIC_TRANSPORT_CARRIERS_FAILURE);
      });
  },

  /**
   * @param commit
   * @param dispatch
   * @param transportId
   * @param payload
   * @returns {Promise<T | never>}
   */
  getNonDynamicTransport({ commit, dispatch }, transportId) {
    commit(C.NON_DYNAMIC_TRANSPORT_GET_TRANSPORT_PENDING);
    return TwentyService.getNonDynamicTransport(transportId)
      .then(res => {
        commit(C.NON_DYNAMIC_TRANSPORT_GET_TRANSPORT_SUCCESS, res.data);
        dispatch('snackbar/show', {
          identifier: 'success',
          message: 'Successfully loaded Non Dynamic Transport',
        }, { root: true });
        dispatch('getTransportLuggage', res.data.id);
        dispatch('getTransportContractsByTransportId', res.data.id);
      }).catch(() => {
        commit(C.NON_DYNAMIC_TRANSPORT_GET_TRANSPORT_FAILURE);
        dispatch('snackbar/show', {
          identifier: 'error',
          message: 'Faild to load Non Dynamic Transport',
        }, { root: true });
      });
  },

  /**
   * @param commit
   * @param withDeleted
   * @returns {Promise<T | never>}
   */
  getNonDynamicTransports({ commit }, withDeleted) {
    function buildTransport(bound, days) {
      return {
        departure: bound[0].departureTransit.iataCode,
        departureFullname: bound[0].departureTransit.name,
        arrival: bound[bound.length - 1].arrivalTransit.iataCode,
        arrivalFullname: bound[bound.length - 1].arrivalTransit.name,
        stopOver: bound.slice(1, bound.length).map(
          transit => transit.departureTransit.iataCode,
        ).join(', '),
        stopOverFullname: bound.slice(1, bound.length).map(
          transit => transit.departureTransit.name,
        ).join(', '),
        days,
        carrier: bound[0].carrier.name,
        ref: bound[0].ref,
      };
    }

    commit(C.NON_DYNAMIC_TRANSPORT_GET_LIST_PENDING);
    return TwentyService.getNonDynamicTransports(withDeleted)
      .then(res => {
        const transports = [];
        res.data.forEach(obj => {
          const transport = {
            id: obj.id,
            type: obj.type,
            allotment: obj.allotment,
            fromDate: null,
            toDate: null,
            PNR: null,
            route: [],
            bound: '',
            itineraries: obj.itineraryIds,
            deleted: obj.deleted,
          };

          if (obj.outbound.length > 0 && obj.inbound.length > 0) {
            transport.bound = 'out-/inbound';
          } else if (obj.outbound.length) {
            transport.bound = 'outbound';
          } else if (obj.inbound.length) {
            transport.bound = 'inbound';
          }

          if (obj.allotments.length === 0) {
            transports.push(transport);
          } else {
            obj.allotments.forEach(allotment => {
              const allotTransport = {
                ...transport,
                fromDate: allotment.fromDate,
                toDate: allotment.toDate,
                PNR: allotment.PNR,
                route: [],
              };
              if (obj.outbound.length) {
                allotTransport.route.push(buildTransport(obj.outbound, allotment.days.outbound));
              }
              if (obj.inbound.length) {
                allotTransport.route.push(buildTransport(obj.inbound, allotment.days.inbound));
              }
              transports.push(allotTransport);
            });
          }
        });
        commit(C.NON_DYNAMIC_TRANSPORT_GET_LIST_SUCCESS, transports);
      }).catch(() => {
        commit(C.NON_DYNAMIC_TRANSPORT_GET_LIST_FAILURE);
      });
  },

  /**
   * @param commit
   * @param dispatch
   * @param payload
   * @returns {Promise<T | never>}
   */
  saveBaseTransport({ commit, dispatch }, payload) {
    commit(C.NON_DYNAMIC_TRANSPORT_SAVE_TRANSPORT_PENDING);
    return TwentyService.saveBaseTransport(payload)
      .then(res => {
        commit(C.NON_DYNAMIC_TRANSPORT_SAVE_TRANSPORT_SUCCESS, res.data);
        dispatch('snackbar/show', {
          identifier: 'success',
          message: 'Successfully saved / updated transport',
        }, { root: true });
      }).catch(() => {
        commit(C.NON_DYNAMIC_TRANSPORT_SAVE_TRANSPORT_FAILURE);
        dispatch('snackbar/show', {
          identifier: 'error',
          message: 'Failed to save / update transport - make sure all fields has been set (Inbound can be left empty)',
        }, { root: true });
      });
  },

  /**
   * @param commit
   * @param dispatch
   * @param payload
   * @returns {Promise<T | never>}
   */
  saveSiblingTransport({ commit, dispatch }, payload) {
    commit(C.NON_DYNAMIC_TRANSPORT_SAVE_SIBLING_TRANSPORT_PENDING);
    return TwentyService.saveTransport(payload)
      .then(res => {
        commit(C.NON_DYNAMIC_TRANSPORT_SAVE_SIBLING_TRANSPORT_SUCCESS, res.data);
        dispatch('snackbar/show', {
          identifier: 'success',
          message: 'Successfully saved / updated sibling transport',
        }, { root: true });
      }).catch(() => {
        commit(C.NON_DYNAMIC_TRANSPORT_SAVE_SIBLING_TRANSPORT_FAILURE);
        dispatch('snackbar/show', {
          identifier: 'error',
          message: 'Failed to save / update sibling transport - make sure all fields has been set',
        }, { root: true });
      });
  },

  /**
   * @param commit
   * @param dispatch
   * @param payload
   * @returns {Promise<T | never>}
   */
  uploadContracts({ commit, dispatch }, payload) {
    commit(C.NON_DYNAMIC_TRANSPORT_UPLOAD_CONTRACTS_PENDING);
    const { contracts, transportId } = payload;
    const formData = new FormData();
    contracts.forEach(contract => {
      formData.append('files[]', contract);
    });
    return TwentyService.uploadContracts(transportId, formData)
      .then(res => {
        if (res.status === 204) {
          commit(C.NON_DYNAMIC_TRANSPORT_UPLOAD_CONTRACTS_SUCCESS);
          dispatch('snackbar/show', {
            identifier: 'success',
            message: 'Successfully uploaded document',
          }, { root: true });
        }
      }).catch(() => {
        commit(C.NON_DYNAMIC_TRANSPORT_UPLOAD_CONTRACTS_FAILURE);
        dispatch('snackbar/show', {
          identifier: 'error',
          message: 'Failed to upload document',
        }, { root: true });
      });
  },

  /**
   * @param commit
   * @returns {Promise<T | never>}
   */
  getTransportTripSeatingSectionTypes({ commit }) {
    commit(C.NON_DYNAMIC_TRANSPORT_SAVE_SIBLING_TRANSPORT_PENDING);
    return TwentyService.getTransportTripSeatingSectionTypes()
      .then(res => {
        if (res.status === 200) {
          commit(C.NON_DYNAMIC_TRANSPORT_SEATING_SECTION_TYPES_SUCCESS, res.data);
        } else {
          commit(C.NON_DYNAMIC_TRANSPORT_SEATING_SECTION_TYPES_NO_CONTENT);
        }
      }).catch(() => {
        commit(C.NON_DYNAMIC_TRANSPORT_SEATING_SECTION_TYPES_FAILURE);
      });
  },

  /**
   * @param commit
   * @returns {Promise<T | never>}
   */
  getTransportTripConsumableTypes({ commit }) {
    commit(C.NON_DYNAMIC_TRANSPORT_CONSUMABLE_TYPES_PENDING);
    return TwentyService.getTransportTripConsumableTypes()
      .then(res => {
        if (res.status === 200) {
          commit(C.NON_DYNAMIC_TRANSPORT_CONSUMABLE_TYPES_SUCCESS, res.data);
        } else {
          commit(C.NON_DYNAMIC_TRANSPORT_CONSUMABLE_TYPES_NO_CONTENT);
        }
      }).catch(() => {
        commit(C.NON_DYNAMIC_TRANSPORT_CONSUMABLE_TYPES_FAILURE);
      });
  },

  /**
   * @param commit
   * @returns {Promise<T | never>}
   */
  getTransportTripUpgradeTypes({ commit }) {
    commit(C.NON_DYNAMIC_TRANSPORT_UPGRADE_TYPES_PENDING);
    return TwentyService.getTransportTripUpgradeTypes()
      .then(res => {
        if (res.status === 200) {
          commit(C.NON_DYNAMIC_TRANSPORT_UPGRADE_TYPES_SUCCESS, res.data);
        } else {
          commit(C.NON_DYNAMIC_TRANSPORT_UPGRADE_TYPES_NO_CONTENT);
        }
      }).catch(() => {
        commit(C.NON_DYNAMIC_TRANSPORT_UPGRADE_TYPES_FAILURE);
      });
  },

  /**
   * @param commit
   * @param dispatch
   * @param payload
   * @returns {Promise<T | never>}
   */
  syncTransportTripSeatings({ commit, dispatch }, payload) {
    commit(C.NON_DYNAMIC_TRANSPORT_SYNC_TRANSPORT_TRIP_SEATINGS_PENDING);
    return TwentyService.syncTransportTripSeatings(payload.tripId, payload.tripSeatings)
      .then(res => {
        commit(C.NON_DYNAMIC_TRANSPORT_SYNC_TRANSPORT_TRIP_SEATINGS_SUCCESS, res.data);
        dispatch('snackbar/show', {
          identifier: 'success',
          message: 'Successfully saved / updated trip seatings',
        }, { root: true });
      }).catch(() => {
        commit(C.NON_DYNAMIC_TRANSPORT_SYNC_TRANSPORT_TRIP_SEATINGS_FAILURE);
        dispatch('snackbar/show', {
          identifier: 'error',
          message: 'Failed to saved / updated trip seatings',
        }, { root: true });
      });
  },

  /**
   * @param commit
   * @param dispatch
   * @param payload
   * @returns {Promise<T | never>}
   */
  syncTransportTripConsumables({ commit, dispatch }, payload) {
    commit(C.NON_DYNAMIC_TRANSPORT_SYNC_TRANSPORT_TRIP_CONSUMABLES_PENDING);
    return TwentyService.syncTransportTripConsumables(payload.tripId, payload.tripConsumables)
      .then(res => {
        commit(C.NON_DYNAMIC_TRANSPORT_SYNC_TRANSPORT_TRIP_CONSUMABLES_SUCCESS, res.data);
        dispatch('snackbar/show', {
          identifier: 'success',
          message: 'Successfully saved / updated trip consumables',
        }, { root: true });
      }).catch(() => {
        commit(C.NON_DYNAMIC_TRANSPORT_SYNC_TRANSPORT_TRIP_CONSUMABLES_FAILURE);
        dispatch('snackbar/show', {
          identifier: 'error',
          message: 'Failed to saved / updated trip consumables',
        }, { root: true });
      });
  },

  /**
   * @param commit
   * @param dispatch
   * @param payload
   * @returns {Promise<T | never>}
   */
  syncTransportTripUpgrades({ commit, dispatch }, payload) {
    commit(C.NON_DYNAMIC_TRANSPORT_SYNC_TRANSPORT_TRIP_UPGRADES_PENDING);
    return TwentyService.syncTransportTripUpgrades(payload.tripId, payload.tripUpgrades)
      .then(res => {
        commit(C.NON_DYNAMIC_TRANSPORT_SYNC_TRANSPORT_TRIP_UPGRADES_SUCCESS, res.data);
        dispatch('snackbar/show', {
          identifier: 'success',
          message: 'Successfully saved / updated trip upgrades',
        }, { root: true });
      }).catch(() => {
        commit(C.NON_DYNAMIC_TRANSPORT_SYNC_TRANSPORT_TRIP_UPGRADES_FAILURE);
        dispatch('snackbar/show', {
          identifier: 'error',
          message: 'Failed to saved / updated trip upgrades',
        }, { root: true });
      });
  },

  /**
   * @param commit
   * @param payload
   * @returns {Promise<T | never>}
   */
  getTransportTripSeatingsByTripId({ commit }, payload) {
    commit(C.NON_DYNAMIC_TRANSPORT_GET_TRANSPORT_TRIP_SEATINGS_PENDING);
    return TwentyService.getTransportTripSeatingsByTripId(payload)
      .then(res => {
        if (res.status === 200) {
          commit(C.NON_DYNAMIC_TRANSPORT_GET_TRANSPORT_TRIP_SEATINGS_SUCCESS, res.data);
        } else {
          commit(C.NON_DYNAMIC_TRANSPORT_GET_TRANSPORT_TRIP_SEATINGS_NO_CONTENT);
        }
      }).catch(() => {
        commit(C.NON_DYNAMIC_TRANSPORT_GET_TRANSPORT_TRIP_SEATINGS_FAILURE);
      });
  },

  /**
   * @param commit
   * @param payload
   * @returns {Promise<T | never>}
   */
  getTransportTripConsumablesByTripId({ commit }, payload) {
    commit(C.NON_DYNAMIC_TRANSPORT_GET_TRANSPORT_TRIP_CONSUMABLES_PENDING);
    return TwentyService.getTransportTripConsumablesByTripId(payload)
      .then(res => {
        if (res.status === 200) {
          commit(C.NON_DYNAMIC_TRANSPORT_GET_TRANSPORT_TRIP_CONSUMABLES_SUCCESS, res.data);
        } else {
          commit(C.NON_DYNAMIC_TRANSPORT_GET_TRANSPORT_TRIP_CONSUMABLES_NO_CONTENT);
        }
      }).catch(() => {
        commit(C.NON_DYNAMIC_TRANSPORT_GET_TRANSPORT_TRIP_CONSUMABLES_FAILURE);
      });
  },

  /**
   * @param commit
   * @param payload
   * @returns {Promise<T | never>}
   */
  getTransportTripUpgradesByTripId({ commit }, payload) {
    commit(C.NON_DYNAMIC_TRANSPORT_GET_TRANSPORT_TRIP_UPGRADES_PENDING);
    return TwentyService.getTransportTripUpgradesByTripId(payload)
      .then(res => {
        if (res.status === 200) {
          commit(C.NON_DYNAMIC_TRANSPORT_GET_TRANSPORT_TRIP_UPGRADES_SUCCESS, res.data);
        } else {
          commit(C.NON_DYNAMIC_TRANSPORT_GET_TRANSPORT_TRIP_UPGRADES_NO_CONTENT);
        }
      }).catch(() => {
        commit(C.NON_DYNAMIC_TRANSPORT_GET_TRANSPORT_TRIP_UPGRADES_FAILURE);
      });
  },

  /**
   * @param commit
   * @returns {Promise<T | never>}
   */
  getTransportLuggageTypes({ commit }) {
    commit(C.NON_DYNAMIC_TRANSPORT_GET_LUGGAGE_TYPES_PENDING);
    return TwentyService.getTransportLuggageTypes()
      .then(res => {
        commit(C.NON_DYNAMIC_TRANSPORT_GET_LUGGAGE_TYPES_SUCCESS, res.data);
      }).catch(() => {
        commit(C.NON_DYNAMIC_TRANSPORT_GET_LUGGAGE_TYPES_FAILURE);
      });
  },

  /**
   * @param commit
   * @param dispatch
   * @param payload
   * @returns {Promise<T | never>}
   */
  syncTransportLuggage({ commit, dispatch }, payload) {
    commit(C.NON_DYNAMIC_TRANSPORT_SYNC_TRANSPORT_LUGGAGE_PENDING);
    return TwentyService.syncTransportLuggage(payload.transportId, payload.transportLuggage)
      .then(res => {
        commit(C.NON_DYNAMIC_TRANSPORT_SYNC_TRANSPORT_LUGGAGE_SUCCESS, res.data);
        dispatch('snackbar/show', {
          identifier: 'success',
          message: 'Successfully saved / updated luggage',
        }, { root: true });
      }).catch(() => {
        commit(C.NON_DYNAMIC_TRANSPORT_SYNC_TRANSPORT_LUGGAGE_FAILURE);
        dispatch('snackbar/show', {
          identifier: 'error',
          message: 'Failed to sync transport luggage, make sure all required input fields is set',
        }, { root: true });
      });
  },

  /**
   * @param commit
   * @param payload
   * @returns {Promise<T | never>}
   */
  getTransportLuggage({ commit }, payload) {
    commit(C.NON_DYNAMIC_TRANSPORT_GET_LUGGAGE_PENDING);
    return TwentyService.getTransportLuggage(payload).then(res => {
      if (res.status === 204) {
        commit(C.NON_DYNAMIC_TRANSPORT_GET_LUGGAGE_NO_CONTENT);
      } else {
        commit(C.NON_DYNAMIC_TRANSPORT_GET_LUGGAGE_SUCCESS, res.data);
      }
    }).catch(() => {
      commit(C.NON_DYNAMIC_TRANSPORT_GET_LUGGAGE_FAILURE);
    });
  },

  /**
   * @param commit
   * @param payload
   * @returns {Promise<T | never>}
   */
  getTransportContractsByTransportId({ commit }, payload) {
    commit(C.NON_DYNAMIC_TRANSPORT_GET_CONTRACTS_PENDING);
    return TwentyService.getTransportContractsByTransportId(payload)
      .then(res => {
        if (res.status === 200) {
          commit(C.NON_DYNAMIC_TRANSPORT_GET_CONTRACTS_SUCCESS, res.data);
        } else if (res.status === 204) {
          commit(C.NON_DYNAMIC_TRANSPORT_GET_CONTRACTS_NO_CONTENT);
        }
      }).catch(() => {
        commit(C.NON_DYNAMIC_TRANSPORT_GET_CONTRACTS_FAILURE);
      });
  },

  /**
   * @param commit
   * @param dispatch
   * @param payload
   * @returns {Promise<T | never>}
   */
  searchTransports({ commit, dispatch }, payload) {
    commit(C.NON_DYNAMIC_TRANSPORT_SEARCH_PENDING);
    return TwentyService.searchTransport(payload)
      .then(res => {
        if (res.status === 200) {
          commit(C.NON_DYNAMIC_TRANSPORT_SEARCH_SUCCESS, res.data);
        } else if (res.status === 204) {
          commit(C.NON_DYNAMIC_TRANSPORT_SEARCH_NO_CONTENT);
          dispatch('snackbar/show', {
            identifier: 'warning',
            message: 'No results found',
          }, { root: true });
        }
      }).catch(() => {
        commit(C.NON_DYNAMIC_TRANSPORT_SEARCH_FAILURE);
        dispatch('snackbar/show', {
          identifier: 'error',
          message: 'Search failed',
        }, { root: true });
      });
  },

  /**
   * @param commit
   * @param dispatch
   * @param payload
   * @returns {Promise<T | never>}
   */
  searchTransportPriceSettings({ commit, dispatch }, payload) {
    commit(C.NON_DYNAMIC_TRANSPORT_PRICE_SETTING_SEARCH_PENDING);
    return TwentyService.searchTransport(payload)
      .then(res => {
        if (res.status === 200) {
          commit(C.NON_DYNAMIC_TRANSPORT_PRICE_SETTING_SEARCH_SUCCESS, res.data);
        } else if (res.status === 204) {
          commit(C.NON_DYNAMIC_TRANSPORT_PRICE_SETTING_SEARCH_NO_CONTENT);
          dispatch('snackbar/show', {
            identifier: 'warning',
            message: 'No results found',
          }, { root: true });
        }
      }).catch(() => {
        commit(C.NON_DYNAMIC_TRANSPORT_PRICE_SETTING_SEARCH_NO_CONTENT);
        dispatch('snackbar/show', {
          identifier: 'error',
          message: 'Search failed',
        }, { root: true });
      });
  },

  /**
   * @param commit
   * @param dispatch
   * @param payload
   * @returns {Promise<T | never>}
   */
  deleteTransport({ dispatch }, payload) {
    return TwentyService.deleteTransport(payload)
      .then(res => {
        if (res.status === 204) {
          location.href = '/non-dynamic-transportation/overview';
        }
      }).catch(() => {
        dispatch('snackbar/show', {
          identifier: 'error',
          message: 'Delete failed',
        }, { root: true });
      });
  },
};



// WEBPACK FOOTER //
// ./src/store/modules/non-dynamic-transport/actions.js